import React from "react";
import "./BusinessSubmissionDetails.css";

const EMPTY_VALUE = "Not provided";

const CATEGORY_LABELS = {
  foodtruck: "Food truck",
  groceries: "Groceries",
  restaurants: "Restaurants",
  restaurant: "Restaurant",
  "child care": "Child care",
  "elderly care": "Elderly care",
  "event management": "Event management",
  "fitness training": "Fitness training",
  "home help": "Home help",
  "it services": "IT services",
  "legal advice": "Legal advice",
  "personal shopping": "Personal shopping",
  "repair services": "Repair services",
  "student services": "Student services",
  "worship places": "Worship places",
};

function formatLabel(value) {
  const cleaned = String(value || "").trim();

  if (!cleaned) {
    return EMPTY_VALUE;
  }

  const mapped = CATEGORY_LABELS[cleaned.toLowerCase()];

  if (mapped) {
    return mapped;
  }

  return cleaned
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatDate(value) {
  if (!value) {
    return EMPTY_VALUE;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function normalizeList(value, additionalValue) {
  const source = Array.isArray(value) ? value : [];
  const values = source
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  const extra = String(additionalValue || "").trim();

  if (extra && !values.some((item) => item.toLowerCase() === extra.toLowerCase())) {
    values.push(extra);
  }

  return Array.from(new Set(values));
}

function DetailItem({ label, value, wide = false, children }) {
  const content = children ?? value ?? EMPTY_VALUE;

  return (
    <div className={`business-submission-detail${wide ? " is-wide" : ""}`}>
      <dt>{label}</dt>
      <dd>{content}</dd>
    </div>
  );
}

function TagList({ label, values, emptyText }) {
  return (
    <div className="business-submission-tag-group">
      <span className="business-submission-tag-label">{label}</span>

      {values.length > 0 ? (
        <div className="business-submission-tags">
          {values.map((value) => (
            <span className="business-submission-tag" key={value}>
              {formatLabel(value)}
            </span>
          ))}
        </div>
      ) : (
        <span className="business-submission-empty-value">{emptyText}</span>
      )}
    </div>
  );
}

export default function BusinessSubmissionDetails({ business }) {
  const record = business || {};

  const communities = normalizeList(
    record.communities,
    record.other_community
  );

  const languages = normalizeList(record.languages, record.other_language);

  const businessType =
    record.other_business_type || record.business_type || record.category;

  const plan = record.listing_plan
    ? formatLabel(record.listing_plan)
    : "Not recorded (older listing)";

  const phoneDigits = String(record.phoneNumber || "").replace(/\D/g, "");

  return (
    <section
      className="business-submission-details"
      aria-label={`Complete submission details for ${record.name || "business"}`}
    >
      <dl className="business-submission-detail-grid">
        <DetailItem label="Listing plan" value={plan} />
        <DetailItem label="Business type" value={formatLabel(businessType)} />
        <DetailItem label="API category" value={formatLabel(record.category)} />
        <DetailItem label="Current status" value={formatLabel(record.status)} />

        <DetailItem label="Phone number">
          {record.phoneNumber ? (
            <a href={`tel:${phoneDigits || record.phoneNumber}`}>
              {record.phoneNumber}
            </a>
          ) : (
            EMPTY_VALUE
          )}
        </DetailItem>

        <DetailItem label="Street address" value={record.street_address} />
        <DetailItem label="City" value={record.city} />
        <DetailItem label="State" value={record.state} />
        <DetailItem label="Full address" value={record.address} wide />

        {record.other_business_type && (
          <DetailItem
            label="Specified business type"
            value={record.other_business_type}
            wide
          />
        )}

        {record.other_community && (
          <DetailItem
            label="Specified community"
            value={record.other_community}
            wide
          />
        )}

        {record.other_language && (
          <DetailItem
            label="Specified language"
            value={record.other_language}
            wide
          />
        )}

        <DetailItem
          label="Submission source"
          value={formatLabel(record.source)}
        />
        <DetailItem label="Submitted" value={formatDate(record.createdAt)} />
        <DetailItem label="Last updated" value={formatDate(record.updatedAt)} />
        <DetailItem label="Record ID" value={record.id} wide />
      </dl>

      <div className="business-submission-tag-sections">
        <TagList
          label="Community focus"
          values={communities}
          emptyText="No community selected"
        />

        <TagList
          label="Languages besides English"
          values={languages}
          emptyText="No additional language selected"
        />
      </div>

      <details className="business-submission-raw">
        <summary>View complete raw submission</summary>
        <pre>{JSON.stringify(record, null, 2)}</pre>
      </details>
    </section>
  );
}
